import { useState, useEffect, useCallback, useRef } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useToast } from '../components/Toast';
import { api, getAccessToken } from '../api/client';
import { useProveedores } from '../hooks/useProveedores';
import { Modal } from '../components/Modal';
import { BuscadorSku, type SkuSugerido } from '../components/BuscadorSku';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { asegurarArticulos, recordarIdentidad } from '../lib/identidadArticulos';

// Module functions: the pickers need them stable.
const buscarArticulos = (texto: string) => api.get<SkuSugerido[]>(`/api/v3/catalogo/buscar?q=${encodeURIComponent(texto)}`);
const resolverArticulos = (codigo: string) => api.get<SkuSugerido[]>(`/api/v3/catalogo/buscar?codigos=${encodeURIComponent(codigo)}`);

interface Insumo {
  id?: number;
  codigoProveedor: string;
  cantidad: number;
  orden: number;
}

interface SkuRow {
  skuInterno: string;
  titulo: string | null;
  marca: string | null;
  proveedorNombre: string | null;
  insumos: Insumo[];
}

interface SkuPendiente {
  skuInterno: string;
  titulo: string | null;
  marca: string | null;
  proveedorNombre: string | null;
  sinMarca: boolean;
  sinProveedor: boolean;
  sinCodigos: boolean;
}

interface Filtros { marcas: string[]; proveedores: string[] }

interface BomComponenteForm { skuHijo: string; cantidad: number; marca: string; proveedorEstandarId: string }

type Tab = 'todos' | 'pendientes';
const PAGE_SIZES = [25, 50, 100];

export function InsumosPage() {
  const { data: proveedoresList } = useProveedores();
  const [tab, setTab] = useState<Tab>('todos');

  // Filtros
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [marca, setMarca] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [sinMarca, setSinMarca] = useState(false);
  const [sinProveedor, setSinProveedor] = useState(false);
  const [sinCodigo, setSinCodigo] = useState(false);
  const [filtros, setFiltros] = useState<Filtros>({ marcas: [], proveedores: [] });

  // Paginación
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  // Datos
  const [rows, setRows] = useState<SkuRow[]>([]);
  const [pendientes, setPendientes] = useState<SkuPendiente[]>([]);
  const [loading, setLoading] = useState(false);

  // Edición inline
  const [expandedSku, setExpandedSku] = useState<string | null>(null);
  const [editingInsumos, setEditingInsumos] = useState<Insumo[]>([]);
  const [editingMeta, setEditingMeta] = useState<{ marca: string; proveedorNombre: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const [exporting, setExporting] = useState(false);
  const toast = useToast();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Modal "+ Nuevo SKU / BOM"
  const [bomModalOpen, setBomModalOpen] = useState(false);
  const [bomSkuPadre, setBomSkuPadre] = useState('');
  const [bomMarcaPadre, setBomMarcaPadre] = useState('');
  const [bomProveedorEstandarIdPadre, setBomProveedorEstandarIdPadre] = useState('');
  const [bomComponentes, setBomComponentes] = useState<BomComponenteForm[]>([{ skuHijo: '', cantidad: 1, marca: '', proveedorEstandarId: '' }]);
  const [creatingBom, setCreatingBom] = useState(false);
  // One creation at a time: a second click during the code lookup must not post twice.
  const creandoBom = useRef(false);
  const cierreBom = useCierreConfirmado(
    useHayCambios({ bomSkuPadre, bomMarcaPadre, bomProveedorEstandarIdPadre, bomComponentes }, bomModalOpen),
    () => setBomModalOpen(false),
  );

  // Debounce búsqueda
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 400);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  // Reset página al cambiar filtros
  useEffect(() => { setPage(1); }, [marca, proveedor, sinMarca, sinProveedor, sinCodigo]);

  // Cargar filtros disponibles una sola vez
  useEffect(() => {
    api.get<Filtros>('/api/v3/insumos/filtros').then(r => setFiltros(r as any)).catch(() => {});
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set('q', debouncedQ);
      if (marca)       params.set('marca', marca);
      if (proveedor)   params.set('proveedor', proveedor);
      if (sinMarca)    params.set('sinMarca', 'true');
      if (sinProveedor) params.set('sinProveedor', 'true');
      if (sinCodigo)   params.set('sinCodigo', 'true');
      params.set('page', String(page));
      params.set('limit', String(limit));

      const res = await api.get(`/api/v3/insumos?${params}`) as any;
      setRows(res.data ?? []);
      setTotal(res.total ?? 0);
    } catch {
      toast.addToast('Error al cargar insumos', 'error');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, marca, proveedor, sinMarca, sinProveedor, sinCodigo, page, limit]);

  const fetchPendientes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v3/insumos/pendientes') as any;
      setPendientes(Array.isArray(res) ? res : (res.data ?? []));
    } catch {
      toast.addToast('Error al cargar pendientes', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'todos') fetchRows();
    else fetchPendientes();
  }, [tab, fetchRows, fetchPendientes]);

  async function handleExport() {
    setExporting(true);
    try {
      const token = getAccessToken();
      const res = await fetch('/api/v3/insumos/export', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Error al exportar');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fecha = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `02_CODIGOS_BD_${fecha}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.addToast('Error al exportar', 'error');
    } finally {
      setExporting(false);
    }
  }

  function openEdit(row: SkuRow) {
    setExpandedSku(row.skuInterno);
    setEditingInsumos(row.insumos.length > 0 ? row.insumos.map(i => ({ ...i })) : [{ codigoProveedor: '', cantidad: 1, orden: 1 }]);
    setEditingMeta({ marca: row.marca ?? '', proveedorNombre: row.proveedorNombre ?? '' });
  }

  function closeEdit() { setExpandedSku(null); setEditingInsumos([]); setEditingMeta(null); }

  function addInsumo() {
    setEditingInsumos(prev => [...prev, { codigoProveedor: '', cantidad: 1, orden: prev.length + 1 }]);
  }

  function removeInsumo(idx: number) {
    setEditingInsumos(prev => prev.filter((_, i) => i !== idx).map((x, i) => ({ ...x, orden: i + 1 })));
  }

  function updateInsumo(idx: number, field: keyof Insumo, value: string) {
    setEditingInsumos(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      if (field === 'cantidad') return { ...item, cantidad: parseFloat(value) || 0 };
      return { ...item, [field]: value };
    }));
  }

  async function saveInsumos(skuInterno: string) {
    setSaving(true);
    try {
      const validInsumos = editingInsumos.filter(i => i.codigoProveedor.trim() !== '');
      // Mandamos skuInterno en el body para evitar que nginx rompa el %2F en la URL
      const skuParam = encodeURIComponent(skuInterno.replace(/\//g, '_SLASH_'));
      if (editingMeta) await api.patch(`/api/v3/insumos/${skuParam}/meta`, { ...editingMeta, skuInterno });
      await api.put(`/api/v3/insumos/${skuParam}/insumos`, {
        skuInterno,
        insumos: validInsumos.map((i, idx) => ({ ...i, orden: idx + 1 })),
      });
      toast.addToast('Guardado correctamente', 'success');
      closeEdit();
      fetchRows();
    } catch {
      toast.addToast('Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  }

  function openBomModal() {
    setBomSkuPadre('');
    setBomMarcaPadre('');
    setBomProveedorEstandarIdPadre('');
    setBomComponentes([{ skuHijo: '', cantidad: 1, marca: '', proveedorEstandarId: '' }]);
    setBomModalOpen(true);
  }

  function closeBomModal() { setBomModalOpen(false); }

  function addBomComponente() {
    setBomComponentes(prev => [...prev, { skuHijo: '', cantidad: 1, marca: '', proveedorEstandarId: '' }]);
  }

  function removeBomComponente(idx: number) {
    setBomComponentes(prev => prev.filter((_, i) => i !== idx));
  }

  function updateBomComponente(idx: number, field: keyof BomComponenteForm, value: string) {
    setBomComponentes(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      if (field === 'cantidad') return { ...item, cantidad: parseFloat(value) || 0 };
      return { ...item, [field]: value };
    }));
  }

  async function handleCrearSkuBom() {
    const conSku = bomComponentes.filter(c => c.skuHijo.trim() !== '');
    if (!bomSkuPadre.trim() || conSku.length === 0) {
      toast.addToast('Completá el SKU y al menos un componente', 'error');
      return;
    }
    if (creandoBom.current) return;
    creandoBom.current = true;
    setCreatingBom(true);
    try {
      // Typed codes not picked: one article is used, several block; an unknown code goes as typed (created «A completar»).
      // A failed lookup blocks: sending the typed text would create a duplicate «A completar» placeholder.
      const { sku: resueltos, ambiguos, fallidos } = await asegurarArticulos([bomSkuPadre, ...conSku.map(c => c.skuHijo)], resolverArticulos);
      if (fallidos.length > 0) {
        toast.addToast('No se pudo verificar el código; probá de nuevo', 'error');
        return;
      }
      if (ambiguos.length > 0) {
        toast.addToast(ambiguos[0], 'error');
        return;
      }
      const resuelto = (t: string) => resueltos.get(t.trim()) ?? t.trim();
      const skuPadre = resuelto(bomSkuPadre);
      const validComponentes = conSku.map(c => ({
        skuHijo: resuelto(c.skuHijo),
        cantidad: c.cantidad,
        marca: c.marca.trim() || undefined,
        proveedorEstandarId: c.proveedorEstandarId ? Number(c.proveedorEstandarId) : undefined,
      }));
      const res = await api.post('/api/v3/insumos/bom', {
        skuPadre,
        marca: bomMarcaPadre.trim() || undefined,
        proveedorEstandarId: bomProveedorEstandarIdPadre ? Number(bomProveedorEstandarIdPadre) : undefined,
        componentes: validComponentes,
      }) as any;
      const creados: string[] = res?.creados ?? [];
      toast.addToast(
        creados.length === 0
          ? 'BOM guardado. No hizo falta crear ningún SKU, ya existían todos.'
          : `SKU creado. ${creados.length} SKU${creados.length !== 1 ? 's' : ''} nuevo${creados.length !== 1 ? 's' : ''} marcado${creados.length !== 1 ? 's' : ''} "A completar".`,
        'success',
      );
      closeBomModal();
    } catch {
      toast.addToast('Error al crear el SKU/BOM', 'error');
    } finally {
      creandoBom.current = false;
      setCreatingBom(false);
    }
  }

  const totalPages = Math.ceil(total / limit);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Insumos / Códigos de proveedor</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={openBomModal}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Nuevo SKU / BOM
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {exporting ? '⏳ Exportando...' : '↓ Exportar Excel'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {(['todos', 'pendientes'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              tab === t
                ? 'border-blue-500 text-blue-600 bg-white dark:bg-gray-900 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}>
            {t === 'todos' ? 'Todos los SKUs' : `Pendientes${tab === 'pendientes' && pendientes.length ? ` (${pendientes.length})` : ''}`}
          </button>
        ))}
      </div>

      {/* ── TAB TODOS ── */}
      {tab === 'todos' && (
        <div className="space-y-3">
          {/* Barra de filtros */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Buscador */}
            <input type="text" placeholder="Buscar SKU, título, marca..." value={q}
              onChange={e => setQ(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg dark:border-gray-600 dark:bg-gray-800 dark:text-white w-56" />

            {/* Marca */}
            <select value={marca} onChange={e => setMarca(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:border-gray-600 dark:bg-gray-800 dark:text-white">
              <option value="">Todas las marcas</option>
              {filtros.marcas.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

            {/* Proveedor */}
            <select value={proveedor} onChange={e => setProveedor(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg dark:border-gray-600 dark:bg-gray-800 dark:text-white">
              <option value="">Todos los proveedores</option>
              {filtros.proveedores.map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            {/* Toggles */}
            <ToggleChip label="Sin marca"     active={sinMarca}     onClick={() => { setSinMarca(v => !v); if (!sinMarca) { setMarca(''); } }} />
            <ToggleChip label="Sin proveedor" active={sinProveedor} onClick={() => { setSinProveedor(v => !v); if (!sinProveedor) setProveedor(''); }} />
            <ToggleChip label="Sin códigos"   active={sinCodigo}    onClick={() => setSinCodigo(v => !v)} />

            {/* Limpiar */}
            {(q || marca || proveedor || sinMarca || sinProveedor || sinCodigo) && (
              <button onClick={() => { setQ(''); setMarca(''); setProveedor(''); setSinMarca(false); setSinProveedor(false); setSinCodigo(false); }}
                className="text-xs text-gray-400 hover:text-red-500 underline">
                Limpiar filtros
              </button>
            )}

            {/* Por página */}
            <div className="ml-auto flex items-center gap-1 text-sm text-gray-500">
              <span>Mostrar</span>
              {PAGE_SIZES.map(s => (
                <button key={s} onClick={() => { setLimit(s); setPage(1); }}
                  className={`px-2 py-0.5 rounded border text-xs ${limit === s ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Contador */}
          {!loading && (
            <p className="text-xs text-gray-400">
              {total === 0 ? 'Sin resultados' : `${total} SKU${total !== 1 ? 's' : ''} — página ${page} de ${totalPages}`}
            </p>
          )}

          {/* Lista */}
          {loading ? (
            <p className="text-gray-400 text-sm py-4">Cargando...</p>
          ) : (
            <div className="space-y-1">
              {rows.map(row => (
                <SkuCard key={row.skuInterno} row={row}
                  expanded={expandedSku === row.skuInterno}
                  editingInsumos={editingInsumos} editingMeta={editingMeta} saving={saving}
                  onOpen={() => openEdit(row)} onClose={closeEdit}
                  onAdd={addInsumo} onRemove={removeInsumo} onUpdate={updateInsumo}
                  onSave={() => saveInsumos(row.skuInterno)}
                  onMetaChange={(f, v) => setEditingMeta(m => m ? { ...m, [f]: v } : m)}
                />
              ))}
            </div>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 pt-2">
              <PageBtn label="«" disabled={page === 1} onClick={() => setPage(1)} />
              <PageBtn label="‹" disabled={page === 1} onClick={() => setPage(p => p - 1)} />
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 3, totalPages - 6));
                const n = start + i;
                return n <= totalPages ? (
                  <PageBtn key={n} label={String(n)} active={n === page} onClick={() => setPage(n)} />
                ) : null;
              })}
              <PageBtn label="›" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
              <PageBtn label="»" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
            </div>
          )}
        </div>
      )}

      {/* ── TAB PENDIENTES ── */}
      {tab === 'pendientes' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            SKUs activos a los que les falta: Marca, Proveedor o Códigos de insumo.
          </p>
          {loading ? (
            <p className="text-gray-400 text-sm">Cargando...</p>
          ) : pendientes.length === 0 ? (
            <p className="text-green-600 dark:text-green-400 text-sm font-medium">✅ No hay pendientes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
                    <th className="px-3 py-2 font-medium">SKU</th>
                    <th className="px-3 py-2 font-medium">Título</th>
                    <th className="px-3 py-2 font-medium">Marca</th>
                    <th className="px-3 py-2 font-medium">Proveedor</th>
                    <th className="px-3 py-2 font-medium">Códigos</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {pendientes.map(p => (
                    <tr key={p.skuInterno} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2 text-xs"><CodigoArticulo sku={p.skuInterno} /></td>
                      <td className="px-3 py-2 max-w-xs truncate text-gray-700 dark:text-gray-300">{p.titulo ?? '—'}</td>
                      <td className="px-3 py-2">{p.sinMarca ? <Falta /> : <span className="text-gray-700 dark:text-gray-300">{p.marca}</span>}</td>
                      <td className="px-3 py-2">{p.sinProveedor ? <Falta /> : <span className="text-gray-700 dark:text-gray-300">{p.proveedorNombre}</span>}</td>
                      <td className="px-3 py-2">{p.sinCodigos ? <span className="text-orange-500 text-xs font-medium">⚠ Sin códigos</span> : <span className="text-green-600 text-xs">✓</span>}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => { setTab('todos'); setQ(p.skuInterno); }}
                          className="text-blue-500 hover:underline text-xs">Editar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal "+ Nuevo SKU / BOM" */}
      {/* Con datos cargados, cerrar (✕, Escape o Cancelar) pide confirmación. */}
      <Modal open={bomModalOpen} cierre={cierreBom} title="Nuevo SKU / BOM" size="md">
            <div className="space-y-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Si el SKU padre o algún componente todavía no existen como artículo, se crean automáticamente marcados "A completar".
              </p>

              <label className="block">
                <span className="text-xs text-gray-500 mb-1 block">SKU</span>
                <BuscadorSku flotante mostrarCodigo mostrarProveedor value={bomSkuPadre} onChange={setBomSkuPadre}
                  onElegir={s => { recordarIdentidad(s); setBomSkuPadre(s.skuInterno); }}
                  buscar={buscarArticulos} resolver={resolverArticulos}
                  placeholder="SKU del artículo padre"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white font-mono" />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Marca</span>
                  <input type="text" value={bomMarcaPadre} onChange={e => setBomMarcaPadre(e.target.value)}
                    placeholder='Sin marca (se usará "A completar")'
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Proveedor Estándar</span>
                  <select value={bomProveedorEstandarIdPadre} onChange={e => setBomProveedorEstandarIdPadre(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white">
                    <option value="">—</option>
                    {(proveedoresList ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Componentes (SKU)</span>
                  <button onClick={addBomComponente} className="text-xs text-blue-500 hover:text-blue-700">+ Agregar</button>
                </div>
                <div className="space-y-2">
                  {bomComponentes.map((c, idx) => (
                    <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-5 text-right shrink-0">{idx + 1}.</span>
                        <div className="flex-1">
                          <BuscadorSku flotante aria-label={`SKU del componente ${idx + 1}`}
                            mostrarCodigo mostrarProveedor value={c.skuHijo} onChange={t => updateBomComponente(idx, 'skuHijo', t)}
                            onElegir={s => { recordarIdentidad(s); updateBomComponente(idx, 'skuHijo', s.skuInterno); }}
                            buscar={buscarArticulos} resolver={resolverArticulos}
                            placeholder="SKU componente"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white font-mono" />
                        </div>
                        <input type="number" value={c.cantidad} min="0" step="0.01" onChange={e => updateBomComponente(idx, 'cantidad', e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white text-right" />
                        <button onClick={() => removeBomComponente(idx)} className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                      </div>
                      <div className="flex items-center gap-2 pl-7">
                        <input type="text" value={c.marca} onChange={e => updateBomComponente(idx, 'marca', e.target.value)}
                          placeholder="Marca (opcional)"
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
                        <select value={c.proveedorEstandarId} onChange={e => updateBomComponente(idx, 'proveedorEstandarId', e.target.value)}
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white">
                          <option value="">—</option>
                          {(proveedoresList ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 mt-4 border-t border-gray-100 dark:border-gray-800">
              <button onClick={cierreBom.cerrar} disabled={creatingBom} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400">Cancelar</button>
              <button onClick={handleCrearSkuBom} disabled={creatingBom} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {creatingBom ? 'Creando...' : 'Crear'}
              </button>
            </div>
      </Modal>
    </div>
  );
}

// ── Pequeños componentes ─────────────────────────────────────────────────────

function ToggleChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 text-xs rounded-full border transition-colors font-medium ${
        active
          ? 'bg-orange-500 text-white border-orange-500'
          : 'border-gray-300 text-gray-600 hover:border-orange-400 dark:border-gray-600 dark:text-gray-400'
      }`}>
      {label}
    </button>
  );
}

function PageBtn({ label, onClick, disabled, active }: { label: string; onClick: () => void; disabled?: boolean; active?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`w-8 h-8 text-xs rounded border transition-colors ${
        active ? 'bg-blue-600 text-white border-blue-600'
               : disabled ? 'text-gray-300 border-gray-200 cursor-default dark:text-gray-600 dark:border-gray-700'
               : 'border-gray-300 hover:border-blue-400 dark:border-gray-600'
      }`}>
      {label}
    </button>
  );
}

function Falta() {
  return <span className="text-red-500 text-xs font-medium">⚠ Falta</span>;
}

// ── SkuCard ──────────────────────────────────────────────────────────────────

interface SkuCardProps {
  row: SkuRow; expanded: boolean;
  editingInsumos: Insumo[]; editingMeta: { marca: string; proveedorNombre: string } | null;
  saving: boolean;
  onOpen: () => void; onClose: () => void; onAdd: () => void;
  onRemove: (idx: number) => void; onUpdate: (idx: number, field: keyof Insumo, value: string) => void;
  onSave: () => void; onMetaChange: (field: 'marca' | 'proveedorNombre', val: string) => void;
}

function SkuCard({ row, expanded, editingInsumos, editingMeta, saving, onOpen, onClose, onAdd, onRemove, onUpdate, onSave, onMetaChange }: SkuCardProps) {
  const hasMissing = !row.marca || !row.proveedorNombre || row.insumos.length === 0;
  return (
    <div className={`border rounded-lg overflow-hidden ${hasMissing ? 'border-orange-200 dark:border-orange-800' : 'border-gray-200 dark:border-gray-700'}`}>
      <button onClick={expanded ? onClose : onOpen}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
        <span className="text-xs text-gray-500 shrink-0 w-32 truncate"><CodigoArticulo sku={row.skuInterno} /></span>
        <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate min-w-0">{row.titulo ?? '—'}</span>
        <span className={`text-xs shrink-0 w-24 truncate ${row.marca ? 'text-gray-500' : 'text-orange-400'}`}>{row.marca ?? '⚠ sin marca'}</span>
        <span className={`text-xs shrink-0 w-28 truncate ${row.proveedorNombre ? 'text-gray-400' : 'text-orange-400'}`}>{row.proveedorNombre ?? '⚠ sin proveedor'}</span>
        <span className={`text-xs shrink-0 w-16 text-right ${row.insumos.length === 0 ? 'text-orange-400' : 'text-gray-400'}`}>
          {row.insumos.length === 0 ? '⚠ 0 cód.' : `${row.insumos.length} cód.`}
        </span>
        <span className="text-gray-400 text-xs shrink-0 ml-1">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && editingMeta && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 bg-white dark:bg-gray-900">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-gray-500 mb-1 block">Marca</span>
              <input type="text" value={editingMeta.marca} onChange={e => onMetaChange('marca', e.target.value)}
                placeholder="Ej: FILGO"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
            </label>
            <label className="block">
              <span className="text-xs text-gray-500 mb-1 block">Proveedor</span>
              <input type="text" value={editingMeta.proveedorNombre} onChange={e => onMetaChange('proveedorNombre', e.target.value)}
                placeholder="Ej: BENNY PEN"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Códigos de insumo</span>
              <button onClick={onAdd} className="text-xs text-blue-500 hover:text-blue-700">+ Agregar</button>
            </div>
            {editingInsumos.length === 0 && <p className="text-xs text-gray-400 italic">Sin códigos. Hacé clic en + Agregar.</p>}
            <div className="space-y-2">
              {editingInsumos.map((ins, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-5 text-right shrink-0">{idx + 1}.</span>
                  <input type="text" value={ins.codigoProveedor} onChange={e => onUpdate(idx, 'codigoProveedor', e.target.value)}
                    placeholder="Código proveedor"
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white font-mono" />
                  <input type="number" value={ins.cantidad} min="0" step="0.01" onChange={e => onUpdate(idx, 'cantidad', e.target.value)}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800 dark:text-white text-right" />
                  <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
            <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400">Cancelar</button>
            <button onClick={onSave} disabled={saving} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
